<template>
  <main class="main-content">
    <!-- Main Hero -->
    <IHeader class="static-background-container _xs:padding-bottom:4 _md:padding-bottom:6">
      <IContainer>
        <IRow middle>
          <!-- Left Column - Text Content -->
          <IColumn xl="6" lg="12" md="12" xs="12">
            <div class="hero__text-content">
              <!-- Dynamic Course Count -->
              <div v-if="landingOptions.show_all_access" class="_margin-bottom:2">
                <IBadge size="lg" class="gradient-badge">
                  <span class="_text:semibold _text:white">
                    All-Access · $79/month · {{ subscriptionCoursesCount }} Course{{
                      subscriptionCoursesCount !== 1 ? 's' : ''
                    }}
                  </span>
                </IBadge>
              </div>

              <h1 class="_text:primary-dark _text:left">
                <span class="heading-wrapper">
                  <div class="responsive-heading">
                    <span class="_text:bold _text:primary-70">
                      Energy transition simplified without the compliance chaos
                    </span>
                  </div>
                </span>
              </h1>
              <p
                class="subtitle _text:dark-40 _text:normal _text:left _xs:padding-x:0 _md:padding-right:4 _lg:padding-right:0 _xs:hidden _md:visible">
                Climate64 blends practitioner expertise with AI tools so energy professionals can
                cut through policy chaos and move projects forward. Short lessons, deployable frameworks.
              </p>

              <p
                class="subtitle _text:dark-40 _text:normal _text:left _xs:padding-x:0 _sm:padding-x:0 _xs:visible _md:hidden">
                Master AI, policy, and finance from insiders—frameworks you won't find on Coursera.
              </p>

              <div class="hero__buttons _margin-top:2">
                <Link
                  class="button -lg -primary _text:semibold _margin-right:1 _xs:margin-bottom:1 _sm:margin-bottom:0"
                  :href="safeRoute('register', '/register')"
                  alt="Start All-Access at $79/month"
                  aria-label="Start All-Access at $79/month"
                  title="Start All-Access at $79/month">
                  Start All-Access at $79/mo
                </Link>
                <Link
                  class="button -outline -lg _text:semibold _margin-right:1"
                  :href="route('page.courses')"
                  alt="Explore Courses"
                  aria-label="Explore Courses"
                  title="Explore Courses">
                  Explore Courses
                </Link>
              </div>
            </div>
          </IColumn>

          <!-- Right Column - App Screenshot with Animated Grid -->
          <IColumn xl="6" lg="12" md="12" xs="12">
            <div class="hero__preview">
              <div class="hero__preview__container">
                <!-- Animated Grid Background -->
                <div class="animated-grid-background">
                  <svg
                    viewBox="0 0 1896 1284"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="xMidYMid slice">
                    <defs>
                      <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color: #ffffff; stop-opacity: 1" />
                        <stop offset="100%" style="stop-color: #ffffff; stop-opacity: 1" />
                      </linearGradient>
                    </defs>

                    <!-- Background -->
                    <rect width="1896" height="1284" fill="url(#bgGradient)" />

                    <!-- AI Grid overlay -->
                    <g class="ai-overlay" opacity="0.5">
                      <!-- Horizontal lines (thicker, more visible) -->
                      <line
                        class="ai-grid-line grid-line-horizontal-1"
                        x1="0"
                        y1="150"
                        x2="1896"
                        y2="150"
                        stroke="#0EA5E9"
                        stroke-width="3" />
                      <line
                        class="ai-grid-line grid-line-horizontal-2"
                        x1="0"
                        y1="320"
                        x2="1896"
                        y2="320"
                        stroke="#06B6D4"
                        stroke-width="3" />
                      <line
                        class="ai-grid-line grid-line-horizontal-3"
                        x1="0"
                        y1="490"
                        x2="1896"
                        y2="490"
                        stroke="#0EA5E9"
                        stroke-width="3" />
                      <line
                        class="ai-grid-line grid-line-horizontal-4"
                        x1="0"
                        y1="642"
                        x2="1896"
                        y2="642"
                        stroke="#06B6D4"
                        stroke-width="3" />
                      <line
                        class="ai-grid-line grid-line-horizontal-5"
                        x1="0"
                        y1="794"
                        x2="1896"
                        y2="794"
                        stroke="#0EA5E9"
                        stroke-width="3" />
                      <line
                        class="ai-grid-line grid-line-horizontal-6"
                        x1="0"
                        y1="964"
                        x2="1896"
                        y2="964"
                        stroke="#06B6D4"
                        stroke-width="3" />
                      <line
                        class="ai-grid-line grid-line-horizontal-7"
                        x1="0"
                        y1="1134"
                        x2="1896"
                        y2="1134"
                        stroke="#0EA5E9"
                        stroke-width="3" />

                      <!-- Vertical lines (thicker, more visible) -->
                      <line
                        class="ai-grid-line grid-line-vertical-1"
                        x1="237"
                        y1="0"
                        x2="237"
                        y2="1284"
                        stroke="#06B6D4"
                        stroke-width="3" />
                      <line
                        class="ai-grid-line grid-line-vertical-2"
                        x1="474"
                        y1="0"
                        x2="474"
                        y2="1284"
                        stroke="#0EA5E9"
                        stroke-width="3" />
                      <line
                        class="ai-grid-line grid-line-vertical-3"
                        x1="711"
                        y1="0"
                        x2="711"
                        y2="1284"
                        stroke="#06B6D4"
                        stroke-width="3" />
                      <line
                        class="ai-grid-line grid-line-vertical-4"
                        x1="948"
                        y1="0"
                        x2="948"
                        y2="1284"
                        stroke="#0EA5E9"
                        stroke-width="3" />
                      <line
                        class="ai-grid-line grid-line-vertical-5"
                        x1="1185"
                        y1="0"
                        x2="1185"
                        y2="1284"
                        stroke="#06B6D4"
                        stroke-width="3" />
                      <line
                        class="ai-grid-line grid-line-vertical-6"
                        x1="1422"
                        y1="0"
                        x2="1422"
                        y2="1284"
                        stroke="#0EA5E9"
                        stroke-width="3" />
                      <line
                        class="ai-grid-line grid-line-vertical-7"
                        x1="1659"
                        y1="0"
                        x2="1659"
                        y2="1284"
                        stroke="#06B6D4"
                        stroke-width="3" />
                    </g>

                    <!-- Shooting Pulses Layer (no arrowheads) -->
                    <g class="pulses-layer">
                      <!-- Horizontal pulses (left to right) -->
                      <line
                        class="shooting-pulse pulse-h-1"
                        x1="0"
                        y1="150"
                        x2="400"
                        y2="150"
                        stroke="#0EA5E9"
                        stroke-width="4" />
                      <line
                        class="shooting-pulse pulse-h-2"
                        x1="0"
                        y1="490"
                        x2="400"
                        y2="490"
                        stroke="#06B6D4"
                        stroke-width="4" />
                      <line
                        class="shooting-pulse pulse-h-3"
                        x1="0"
                        y1="794"
                        x2="400"
                        y2="794"
                        stroke="#0EA5E9"
                        stroke-width="4" />
                      <line
                        class="shooting-pulse pulse-h-4"
                        x1="0"
                        y1="1134"
                        x2="400"
                        y2="1134"
                        stroke="#06B6D4"
                        stroke-width="4" />

                      <!-- Vertical pulses (top to bottom) -->
                      <line
                        class="shooting-pulse pulse-v-1"
                        x1="474"
                        y1="0"
                        x2="474"
                        y2="350"
                        stroke="#0EA5E9"
                        stroke-width="4" />
                      <line
                        class="shooting-pulse pulse-v-2"
                        x1="948"
                        y1="0"
                        x2="948"
                        y2="350"
                        stroke="#06B6D4"
                        stroke-width="4" />
                      <line
                        class="shooting-pulse pulse-v-3"
                        x1="1422"
                        y1="0"
                        x2="1422"
                        y2="350"
                        stroke="#0EA5E9"
                        stroke-width="4" />

                      <!-- Reverse direction pulses for variety -->
                      <line
                        class="shooting-pulse pulse-h-reverse-1"
                        x1="1896"
                        y1="320"
                        x2="1496"
                        y2="320"
                        stroke="#06B6D4"
                        stroke-width="4" />
                      <line
                        class="shooting-pulse pulse-h-reverse-2"
                        x1="1896"
                        y1="964"
                        x2="1496"
                        y2="964"
                        stroke="#0EA5E9"
                        stroke-width="4" />

                      <line
                        class="shooting-pulse pulse-v-reverse-1"
                        x1="711"
                        y1="1284"
                        x2="711"
                        y2="934"
                        stroke="#06B6D4"
                        stroke-width="4" />
                      <line
                        class="shooting-pulse pulse-v-reverse-2"
                        x1="1185"
                        y1="1284"
                        x2="1185"
                        y2="934"
                        stroke="#0EA5E9"
                        stroke-width="4" />
                    </g>
                  </svg>
                </div>

                <!-- Hero Image (on top of grid) -->
                <img
                  id="appPreviewImage"
                  src="/images/woman_product_esg.png"
                  s
                  alt="Climate64 instructors hero"
                  class="app-preview-image _xs:margin-top:2 _xl:margin-top:0" />
              </div>
            </div>
          </IColumn>
        </IRow>
      </IContainer>
    </IHeader>
  </main>
</template>

<script setup>
  import config from '@config'
  import { Link } from '@inertiajs/vue3'

  // Accept subscription courses count as prop
  const props = defineProps({
    subscriptionCoursesCount: {
      type: Number,
      default: 4,
    },
  })

  // Safe route helper function
  const safeRoute = (routeName, fallbackUrl) => {
    return typeof route !== 'undefined' ? route(routeName) : fallbackUrl
  }

// landing config
  const landingOptions = config.landing
</script>

<style scoped>
  /* ========================================
     HERO LAYOUT & TEXT
     ======================================== */
  .main-content {
    position: relative;
    height: auto;
    padding: 1rem 0 0;
    overflow: visible;
  }

  .hero__text-content {
    padding-right: 1.5rem;
  }

  .hero__buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .responsive-heading {
    line-height: 1;
    font-weight: 500;
    font-size: 40px;
    margin-top: 2rem;
  }

  .heading-wrapper {
    display: inline;
  }

  .subtitle {
    font-size: 1.3rem;
    margin-bottom: 2rem;
  }

  .animated-gradient {
    background: linear-gradient(to right, #ff4242, #adcbee, #009eda);
    background-size: 300% 100%;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent !important;
    animation: gradient-animation 10s ease-in-out infinite;
    white-space: wrap;
  }

  /* Gradient Badge */
  .gradient-badge {
    background: linear-gradient(to right, #ff4242, #0db4f3);
    background-size: 300% 100%;
    border: none;
    animation: gradient-animation 10s ease-in-out infinite;
    display: inline-flex;
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
  }

  .gradient-badge span {
    font-size: 0.95rem;
    letter-spacing: 0.025em;
  }

  @keyframes gradient-animation {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  /* ========================================
     HERO PREVIEW & IMAGE
     ======================================== */
  .hero__preview {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 0;
  }

  .hero__preview__container {
    position: relative;
    top: 40px;
    width: 100%;
    max-width: 800px;
    text-align: center;
  }

  .app-preview-image {
    position: relative;
    z-index: 1;
    width: 100%;
    height: auto;
    transition: transform 0.3s ease;
  }

  .app-preview-image:hover {
    transform: translateY(-5px);
  }

  /* ========================================
     BACKGROUND CONTAINER
     ======================================== */
  .static-background-container {
    background-size: cover;
    background-position: center;
    background-color: #fff;
    align-items: center;
    justify-content: flex-start;
    overflow: hidden;
    will-change: opacity;
    backface-visibility: hidden;
  }

  .static-background-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
  }

  .static-background-container > * {
    position: relative;
    z-index: 1;
  }

  /* ========================================
     ANIMATED GRID BACKGROUND
     ======================================== */
  .animated-grid-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
  }

  .animated-grid-background svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  /* AI Grid Animation */
  .ai-grid-line {
    stroke-dasharray: 10 5;
    animation: gridScan 6s linear infinite;
  }

  @keyframes gridScan {
    from {
      stroke-dashoffset: 0;
    }
    to {
      stroke-dashoffset: 300;
    }
  }

  /* Staggered animation delays for horizontal lines */
  .grid-line-horizontal-1 {
    animation-delay: 0s;
  }
  .grid-line-horizontal-2 {
    animation-delay: 0.5s;
  }
  .grid-line-horizontal-3 {
    animation-delay: 1s;
  }
  .grid-line-horizontal-4 {
    animation-delay: 1.5s;
  }
  .grid-line-horizontal-5 {
    animation-delay: 2s;
  }
  .grid-line-horizontal-6 {
    animation-delay: 2.5s;
  }
  .grid-line-horizontal-7 {
    animation-delay: 3s;
  }

  /* Staggered animation delays for vertical lines */
  .grid-line-vertical-1 {
    animation-delay: 0.3s;
  }
  .grid-line-vertical-2 {
    animation-delay: 0.8s;
  }
  .grid-line-vertical-3 {
    animation-delay: 1.3s;
  }
  .grid-line-vertical-4 {
    animation-delay: 1.8s;
  }
  .grid-line-vertical-5 {
    animation-delay: 2.3s;
  }
  .grid-line-vertical-6 {
    animation-delay: 2.8s;
  }
  .grid-line-vertical-7 {
    animation-delay: 3.3s;
  }

  /* ========================================
     SHOOTING PULSES ANIMATION
     ======================================== */
  .shooting-pulse {
    opacity: 0;
    animation: shootAndFade 4s ease-in-out infinite;
  }

  /* Horizontal pulses (left to right) */
  .pulse-h-1 {
    animation-delay: 0.5s;
  }

  .pulse-h-2 {
    animation-delay: 2s;
  }
  .pulse-h-3 {
    animation-delay: 3.5s;
  }
  .pulse-h-4 {
    animation-delay: 1.2s;
  }

  /* Horizontal reverse pulses (right to left) */
  .pulse-h-reverse-1 {
    animation-delay: 2.8s;
  }
  .pulse-h-reverse-2 {
    animation-delay: 4.2s;
  }

  /* Vertical pulses (top to bottom) */
  .pulse-v-1 {
    animation-delay: 1s;
  }
  .pulse-v-2 {
    animation-delay: 2.5s;
  }
  .pulse-v-3 {
    animation-delay: 4s;
  }

  /* Vertical reverse pulses (bottom to top) */
  .pulse-v-reverse-1 {
    animation-delay: 1.8s;
  }
  .pulse-v-reverse-2 {
    animation-delay: 3.2s;
  }

  @keyframes shootAndFade {
    0% {
      opacity: 0;
      stroke-dasharray: 0 800;
      stroke-dashoffset: 0;
    }
    10% {
      opacity: 0.8;
    }
    40% {
      opacity: 0.8;
      stroke-dasharray: 400 800;
      stroke-dashoffset: -400;
    }
    70% {
      opacity: 0.4;
      stroke-dasharray: 400 800;
      stroke-dashoffset: -800;
    }
    100% {
      opacity: 0;
      stroke-dasharray: 400 800;
      stroke-dashoffset: -1200;
    }
  }

  /* ========================================
     RESPONSIVE BREAKPOINTS
     ======================================== */
  /* Tablet/Phone */
  @media (min-width: 768px) {
    .responsive-heading {
      font-size: 48px;
      margin-top: 2rem;
    }
  }

  /* Small height screens */
  @media (max-height: 640px) {
    .responsive-heading {
      font-size: 32px;
      margin-top: 1.5rem;
    }

    .subtitle {
      font-size: 1rem;
      margin-bottom: 1rem;
    }
  }

  /* Desktop */
  @media (min-width: 1024px) {
    .responsive-heading {
      font-size: 48px;
      margin-top: 2rem;
    }

    .hero__text-content {
      padding-right: 0;
    }

    .hero__preview {
      padding: 1rem 0;
    }

    .hero__preview__container {
      position: relative;
      top: 0px;
    }

    .animated-grid-background {
      opacity: 0.9;
    }

    .shooting-pulse {
      animation-duration: 5s;
    }
  }

  /* Large Desktop */
  @media (min-width: 1200px) {
    .hero__preview__container {
      max-width: 900px;
    }
  }

  /* Mobile */
  @media (max-width: 768px) {
    .hero__buttons {
      justify-content: flex-start;
    }

    .responsive-heading {
      text-align: left !important;
    }

    .subtitle {
      text-align: left !important;
    }

    .static-background-container {
      min-height: 85vh;
    }

    .animated-grid-background {
      opacity: 0.7;
    }

    .shooting-pulse {
      animation-duration: 6s;
    }

    .ai-grid-line {
      stroke-width: 2;
    }

    .gradient-badge {
      padding: 0.4rem 0.75rem;
    }

    .gradient-badge span {
      font-size: 0.85rem;
    }
  }

  @media (max-width: 480px) {
    .hero__buttons {
      flex-direction: column;
      width: 100%;
    }

    .hero__buttons .button,
    .hero__buttons .i-button {
      width: 100%;
      text-align: center;
    }
  }
</style>
